/**
 * Paginated RMA data table for the redesigned Manage Returns list: headers, rows, and pagination.
 * Sorting is controlled from the RMA details column settings popover, not from header clicks.
 * When bulk Mark as Done is enabled, a checkbox column with header tristate select-all is shown.
 */
import { observer } from "mobx-react";
import { useCallback } from "react";
import Table from "../../../../../components/table/table";
import { PAGINATION_BUTTON_COLOR } from "../../../../../components/table/table.types";
import {
    CHECKBOX_SIZES,
    TRISTATE_CHECKBOX_VALUES,
    TristateCheckbox,
} from "../../../../../components/inputs/tristate-checkbox/tristate-checkbox";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import rmaPagedListStore from "../../stores/rma-tabs-paged-list-store";
import RmaListItemStore from "../../../rma-list-page/stores/rma-item-store";
import RmaListTableRow from "./components/rma-list-table-row/rma-list-table-row";
import RmaListTableSettings from "./components/rma-list-table-settings/rma-list-table-settings";
import translationStore from "../../../../../translation/stores/translations-store";
import uiSettingsStore from "../../../../../stores/ui-settings-store";
import bulkRmaActionStore from "../../stores/bulk-rma-action-store";
import rmsMixpanelStore from "../../../../../stores/rms-mixpanel-store";
import "./rma-list-table.scss";

const RmaListTable: React.FC = observer(() => {
    const { getTranslation } = useDashboardTranslation();
    const scope = "dashboard-returns-page";
    const rmaListFiltersStore = rmaPagedListStore.rmaListFiltersStore;
    const bulkSelectionStore = rmaPagedListStore.bulkSelectionStore;
    const bulkEnabled = bulkRmaActionStore.isFeatureEnabled;
    const selectionBlocked = bulkSelectionStore.isBlocked;
    const isLoading =
        rmaPagedListStore.isLoading ||
        rmaListFiltersStore.isLoading ||
        uiSettingsStore.isLoading ||
        uiSettingsStore.isSaving() ||
        translationStore.loadScope('resolutions').isLoading;

    const onPaginationSetPage = useCallback(async (page: number) => {
        rmaPagedListStore.resetIsLoaded();
        window.scrollTo(0, 0);
        await rmaPagedListStore.loadPage(page);
    }, []);

    const headerState = bulkSelectionStore.headerState();
    const onHeaderChange = (next: TRISTATE_CHECKBOX_VALUES) => {
        if (selectionBlocked) {
            return;
        }
        if (next === TRISTATE_CHECKBOX_VALUES.CHECKED) {
            bulkSelectionStore.selectAllMatchingFilters();
            rmsMixpanelStore.trackEvent("d&_bulk_select_all");
            return;
        }
        bulkSelectionStore.clear();
        rmsMixpanelStore.trackEvent("d&_bulk_clear_selection");
    };

    const columns = [
        ...(bulkEnabled
            ? [
                  {
                      content: (
                          <div
                              className="rma-list-table__select-all"
                              onClick={(e) => e.stopPropagation()}
                          >
                              <TristateCheckbox
                                  label=""
                                  size={CHECKBOX_SIZES.SMALL}
                                  value={headerState}
                                  disabled={selectionBlocked || isLoading}
                                  onChange={onHeaderChange}
                              />
                          </div>
                      ),
                      width: { value: 40, type: "px" as const },
                  },
              ]
            : []),
        {
            content: (
                <span className="rma-list-table__header-with-settings">
                    {getTranslation("RMA_DETAILS_COLUMN_HEADER", scope)}
                    <RmaListTableSettings disabled={isLoading} />
                </span>
            ),
            width: { value: 170, type: "px" as const },
        },
        { content: getTranslation("RESOLUTION_COLUMN_HEADER", scope), width: { value: 1.8, type: "fr" as const } },
        { content: getTranslation("ITEMS", scope), width: { value: 100, type: "px" as const } },
        { content: getTranslation("CUSTOMER", scope), width: { value: 1, type: "fr" as const } },
        { content: getTranslation("STATUS", scope), width: { value: 160, type: "px" as const } },
    ];

    return (
        <div className="rma-list-table">
            <div className="rma-list-table__inner rma-table">
                <Table
                    isLoading={isLoading}
                    totalResultsTitle="returns"
                    pagination={{
                        onSetPage: onPaginationSetPage,
                        page: rmaPagedListStore.page,
                        total: rmaPagedListStore.total,
                        perPage: rmaPagedListStore.perPage,
                        buttonColor: PAGINATION_BUTTON_COLOR.MUTED,
                    }}
                    columns={columns}
                >
                    {rmaPagedListStore.list.map((rma: RmaListItemStore) => (
                        <RmaListTableRow key={rma.rmaId} rma={rma} />
                    ))}
                </Table>
            </div>
        </div>
    );
});

export default RmaListTable;
